import { Button, Select } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import type { Device, DeviceStatus } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { listDevices } from '../api';
import { BatteryCell, DeviceAlarms, DeviceStatusBadge } from '../components/StatusBadges';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatInteger, formatRelative } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

const STATUSES: DeviceStatus[] = ['online', 'idle', 'stale', 'offline', 'inactive'];

/**
 * The scanner fleet (spec §4.2). Live: every heartbeat writes the `devices`
 * topic, so the list re-fetches itself while you watch it — which is the point
 * of the page. Stale devices are the alarm, so they are visible without
 * filtering: status and alarm chips are the first two things a supervisor sees.
 */
export function DevicesPage(): ReactNode {
  const { t } = useTranslation(['devicesPrinting', 'common']);
  const navigate = useNavigate();
  const { state, setState, setFilter } = useListState({ defaultSort: 'lastSeenAt', defaultDir: 'desc' });

  const query = useQuery({
    queryKey: ['devices', listQueryKey(state)],
    queryFn: () => listDevices(state),
  });
  useLiveQuery(['devices'], ['devices']);

  const columns: DataTableColumn<Device>[] = [
    {
      id: 'serial',
      header: t('devicesPrinting:devices.columns.serial'),
      sortField: 'serial',
      cell: (row) => <span className="font-mono text-xs">{row.serial}</span>,
    },
    { id: 'name', header: t('devicesPrinting:devices.columns.name'), sortField: 'name', cell: (row) => row.name || '—' },
    {
      id: 'status',
      header: t('devicesPrinting:devices.columns.status'),
      cell: (row) => <DeviceStatusBadge status={row.status} />,
    },
    {
      id: 'alarms',
      header: t('devicesPrinting:devices.columns.alarms'),
      cell: (row) => <DeviceAlarms alarms={row.alarms} />,
    },
    {
      id: 'battery',
      header: t('devicesPrinting:devices.columns.battery'),
      align: 'right',
      sortField: 'batteryPct',
      cell: (row) => <BatteryCell pct={row.batteryPct} />,
    },
    {
      id: 'outbox',
      header: t('devicesPrinting:devices.columns.outbox'),
      align: 'right',
      sortField: 'outboxDepth',
      hideBelow: 'md',
      cell: (row) => formatInteger(row.outboxDepth),
    },
    {
      id: 'appVersion',
      header: t('devicesPrinting:devices.columns.appVersion'),
      sortField: 'appVersion',
      hideBelow: 'lg',
      cell: (row) => row.appVersion ?? '—',
    },
    {
      id: 'lastUser',
      header: t('devicesPrinting:devices.columns.lastUser'),
      hideBelow: 'xl',
      cell: (row) => row.lastUserName ?? '—',
    },
    {
      id: 'lastSeen',
      header: t('devicesPrinting:devices.columns.lastSeen'),
      sortField: 'lastSeenAt',
      cell: (row) =>
        row.lastSeenAt ? formatRelative(row.lastSeenAt) : t('devicesPrinting:devices.detail.never'),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('devicesPrinting:devices.title')}
        subtitle={t('devicesPrinting:devices.subtitle')}
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={query.data?.total ?? null}
        loading={query.isLoading}
        error={query.error}
        searchPlaceholder={t('devicesPrinting:devices.searchPlaceholder')}
        emptyTitle={t('devicesPrinting:devices.empty')}
        emptyDescription={t('devicesPrinting:devices.emptyDescription')}
        onRowClick={(row) => void navigate(`/devices/scanners/${row.id}`)}
        filters={
          <>
            <Select
              className="w-48"
              aria-label={t('devicesPrinting:devices.filters.status')}
              value={String(state.filters?.['status'] ?? '')}
              options={[
                { value: '', label: t('devicesPrinting:devices.filters.allStatuses') },
                ...STATUSES.map((status) => ({
                  value: status,
                  label: t(`devicesPrinting:status.${status}`),
                })),
              ]}
              onChange={(event) => {
                setFilter('status', event.target.value || undefined);
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('common:actions.refresh')}
            </Button>
          </>
        }
      />
    </>
  );
}
